/**
 * Owner: Masa Bayu
 * File: LandingHeader.tsx
 * Date:Time: 2026-10-18 16:35
 * Description: Home landing §376 — zon kepala: header jenama + carian pill +
 *   sheet penuras (mobile <768) dan topbar carian/kategori/awal/profil (≥768).
 *   §462 — loceng dibuang daripada header mudah alih; ia tinggal pada topbar
 *   desktop dan pada tab bar bawah (AlertsPanel variant 'bar').
 */

import type { FormEvent, ReactElement, Ref } from 'react';
import { QX_TAGLINE } from '../../../brand/brand-assets';
import { accountEntryPath } from '../../../auth/session-auth';
import { useOptionalPwaPhaseA } from '../../../pwa/PwaPhaseAContext';
import { MARKETPLACE_CATEGORY_ORDER, type MarketplaceCategoryId } from '../marketplace-projection';
import type { LandingLabels } from './landing-locale';
import { LandingIcon } from './LandingIcons';
import { go } from './landing-shared';

export interface LandingHeaderProps {
  readonly t: LandingLabels;
  readonly categoryLabels: Readonly<Record<MarketplaceCategoryId, string>>;
  readonly query: string;
  readonly onQuery: (next: string) => void;
  readonly onSubmitSearch: (event: FormEvent) => void;
  readonly filterCat: MarketplaceCategoryId | 'all';
  readonly onFilterCat: (next: MarketplaceCategoryId | 'all') => void;
  readonly unread: number;
  readonly alertsOpen: boolean;
  readonly alerts: ReadonlyArray<{ readonly id: string; readonly text: string }>;
  readonly onBellToggle: () => void;
  readonly alertsRef: Ref<HTMLDivElement>;
  readonly onNavigate: (path: string) => void;
  readonly signedIn: boolean;
}

function BellButton({
  t,
  unread,
  open,
  alerts,
  onToggle,
  anchorRef,
}: {
  readonly t: LandingLabels;
  readonly unread: number;
  readonly open: boolean;
  readonly alerts: ReadonlyArray<{ readonly id: string; readonly text: string }>;
  readonly onToggle: () => void;
  readonly anchorRef: Ref<HTMLDivElement>;
}): ReactElement {
  return (
    <div className="qxl__bell-wrap" ref={anchorRef}>
      <button
        type="button"
        className="qxl__icon-btn"
        aria-label={t.notificationsAria}
        aria-expanded={open}
        onClick={onToggle}
      >
        <LandingIcon name="bell" />
        {unread > 0 ? <span className="qxl__badge qxl__badge--dot" aria-hidden="true" /> : null}
      </button>
      {open ? <AlertsPanel t={t} alerts={alerts} /> : null}
    </div>
  );
}

/* ── Sheet notifikasi (loceng desktop header / loceng bar bawah §462) ──── */

export function AlertsPanel({
  t,
  alerts,
  variant,
}: {
  readonly t: LandingLabels;
  readonly alerts: ReadonlyArray<{ readonly id: string; readonly text: string }>;
  readonly variant?: 'bar' | undefined;
}): ReactElement {
  return (
    <div
      className={variant === 'bar' ? 'qxl__alerts-panel qxl__alerts-panel--bar' : 'qxl__alerts-panel'}
      role="dialog"
      aria-label={t.alerts.title}
    >
      <p className="qxl__filter-title">{t.alerts.title}</p>
      {alerts.length === 0 ? (
        <p className="qxl__alerts-empty">{t.alerts.empty}</p>
      ) : (
        <ul className="qxl__alerts-list">
          {alerts.map((alert) => (
            <li key={alert.id}>{alert.text}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterCategoryRadio({
  t,
  value,
  categoryLabels,
  onChange,
}: {
  readonly t: LandingLabels;
  readonly value: MarketplaceCategoryId | 'all';
  readonly categoryLabels: Readonly<Record<MarketplaceCategoryId, string>>;
  readonly onChange: (next: MarketplaceCategoryId | 'all') => void;
}): ReactElement {
  return (
    <div className="qxl__filter-options" role="radiogroup" aria-label={t.filterSheet.title}>
      <label className="qxl__filter-option">
        <input
          type="radio"
          name="qxl-filter-cat"
          checked={value === 'all'}
          onChange={() => {
            onChange('all');
          }}
        />
        {t.filterSheet.all}
      </label>
      {MARKETPLACE_CATEGORY_ORDER.map((id) => (
        <label key={id} className="qxl__filter-option">
          <input
            type="radio"
            name="qxl-filter-cat"
            checked={value === id}
            onChange={() => {
              onChange(id);
            }}
          />
          {categoryLabels[id]}
        </label>
      ))}
    </div>
  );
}

/* ── Header jenama + carian pill (mobile <768) ────────────────────────── */

/* §462 — mudah alih tiada loceng header; loceng home kini tab dalam bar bawah. */

function InstallPill({
  t,
  onNavigate,
}: {
  readonly t: LandingLabels;
  readonly onNavigate: (path: string) => void;
}): ReactElement | null {
  const pwa = useOptionalPwaPhaseA();
  if (pwa === null) {
    return null;
  }
  if (pwa.install.mode === 'installing') {
    return (
      <span
        className="qxl__install-pill qxl__install-pill--busy"
        role="status"
        aria-label={t.install.installing}
      >
        <span className="qx-spinner" aria-hidden="true" />
        <span className="visually-hidden">{t.install.installing}</span>
      </span>
    );
  }
  if (pwa.install.mode === 'promptable' && pwa.bipAvailable) {
    return (
      <button
        type="button"
        className="qxl__install-pill"
        onClick={() => {
          void pwa.promptInstall();
        }}
      >
        <LandingIcon name="download" size={14} /> Install
      </button>
    );
  }
  if (pwa.install.mode === 'a2hs_guided') {
    return (
      <button
        type="button"
        className="qxl__install-pill"
        onClick={() => {
          go(onNavigate, '/account');
        }}
      >
        <LandingIcon name="download" size={14} /> Install
      </button>
    );
  }
  return null;
}

export function LandingMobileChrome({
  t,
  categoryLabels,
  query,
  onQuery,
  onSubmitSearch,
  filterCat,
  onFilterCat,
  filterOpen,
  onFilterToggle,
  onFilterApply,
  signedIn,
  onNavigate,
}: LandingHeaderProps & {
  readonly filterOpen: boolean;
  readonly onFilterToggle: () => void;
  readonly onFilterApply: () => void;
}): ReactElement {
  return (
    <>
      <header className="qxl__mheader">
        <div className="qxl__brand">
          <img className="qxl__wordmark-img" src="/brand/qx.png" alt="QX" />
          <span className="qxl__brand-divider" aria-hidden="true" />
          <span className="qxl__brand-text">
            <strong>{QX_TAGLINE.top}</strong>
            <small>{QX_TAGLINE.bottom}</small>
          </span>
        </div>
        <div className="qxl__msearch">
          <form className="qxl__search-pill" onSubmit={onSubmitSearch} role="search">
            <LandingIcon name="search" className="qxl__search-icon" />
            <input
              type="search"
              value={query}
              placeholder={t.searchPlaceholder}
              aria-label={t.searchAria}
              onChange={(event) => {
                onQuery(event.target.value);
              }}
            />
            <button type="submit" className="visually-hidden" tabIndex={-1} aria-hidden="true">
              {t.searchAria}
            </button>
          </form>
          <div className="qxl__filter-wrap">
            <button
              type="button"
              className="qxl__icon-btn qxl__filter-btn"
              aria-label={t.filterAria}
              aria-expanded={filterOpen}
              onClick={onFilterToggle}
            >
              <LandingIcon name="filter" />
            </button>
            {filterOpen ? (
              <div className="qxl__filter-sheet" role="dialog" aria-label={t.filterSheet.title}>
                <p className="qxl__filter-title">{t.filterSheet.title}</p>
                <FilterCategoryRadio
                  t={t}
                  value={filterCat}
                  categoryLabels={categoryLabels}
                  onChange={onFilterCat}
                />
                <button
                  type="button"
                  className="qxl__pill qxl__pill--primary qxl__filter-apply"
                  onClick={onFilterApply}
                >
                  {t.filterSheet.apply}
                </button>
              </div>
            ) : null}
          </div>
        </div>
        <div className="qxl__mheader-actions">
          <InstallPill t={t} onNavigate={onNavigate} />
          <button
            type="button"
            className="qxl__icon-btn"
            aria-label={t.accountAria}
            onClick={() => {
              // §414 — aturan sesi sebenar: tetamu ke login, ahli ke profil reader.
              go(onNavigate, accountEntryPath(signedIn));
            }}
          >
            <LandingIcon name="user" />
          </button>
        </div>
      </header>
    </>
  );
}

/* ── Topbar desktop (≥768) ─────────────────────────────────────────────── */

export function LandingTopBar({
  t,
  categoryLabels,
  query,
  onQuery,
  onSubmitSearch,
  filterCat,
  onFilterCat,
  unread,
  alertsOpen,
  alerts,
  onBellToggle,
  alertsRef,
  cartCount,
  displayName,
  signedIn,
  onNavigate,
}: LandingHeaderProps & {
  readonly cartCount: number;
  readonly displayName: string;
}): ReactElement {
  return (
    <header className="qxl__topbar">
      <button
        type="button"
        className="qxl__wordmark qxl__wordmark--lg"
        aria-label={t.tabs.home}
        onClick={() => {
          go(onNavigate, '/');
        }}
      >
        <img className="qxl__wordmark-img qxl__wordmark-img--lg" src="/brand/qx.png" alt="" />
        <span className="qxl__wordmark-tag" aria-hidden="true">
          <strong>{QX_TAGLINE.top}</strong>
          <small>{QX_TAGLINE.bottom}</small>
        </span>
      </button>
      <form className="qxl__topsearch" onSubmit={onSubmitSearch} role="search">
        <LandingIcon name="search" className="qxl__search-icon" />
        <input
          type="search"
          value={query}
          placeholder={t.searchPlaceholder}
          aria-label={t.searchAria}
          onChange={(event) => {
            onQuery(event.target.value);
          }}
        />
        <select
          className="qxl__topsearch-cat"
          aria-label={t.categoryStrip.ariaLabel}
          value={filterCat}
          onChange={(event) => {
            // §409 — pemilihan kategori menapis rak home di tempat (tiada navigasi).
            onFilterCat(event.target.value as MarketplaceCategoryId | 'all');
          }}
        >
          <option value="all">{t.filterSheet.all}</option>
          {MARKETPLACE_CATEGORY_ORDER.map((id) => (
            <option key={id} value={id}>
              {categoryLabels[id]}
            </option>
          ))}
        </select>
        <button type="submit" className="visually-hidden" tabIndex={-1} aria-hidden="true">
          {t.searchAria}
        </button>
      </form>
      <div className="qxl__topbar-actions">
        <BellButton
          t={t}
          unread={unread}
          open={alertsOpen}
          alerts={alerts}
          onToggle={onBellToggle}
          anchorRef={alertsRef}
        />
        <button
          type="button"
          className="qxl__icon-btn"
          aria-label={t.wishlistAria}
          onClick={() => {
            go(onNavigate, '/library');
          }}
        >
          <LandingIcon name="heart" />
        </button>
        <button
          type="button"
          className="qxl__icon-btn"
          aria-label={`${t.cartAria} (${String(cartCount)})`}
          onClick={() => {
            go(onNavigate, '/cart');
          }}
        >
          <LandingIcon name="cart" />
          {cartCount > 0 ? <span className="qxl__badge">{cartCount}</span> : null}
        </button>
        <button
          type="button"
          className="qxl__profile-chip"
          aria-label={t.accountAria}
          onClick={() => {
            // §414 — chip desktop ikut aturan yang sama (tetamu → login).
            go(onNavigate, accountEntryPath(signedIn));
          }}
        >
          <span className="qxl__avatar" aria-hidden="true">
            <LandingIcon name="user" size={18} />
          </span>
          <span className="qxl__profile-text">
            <strong>{displayName}</strong>
            <small>{t.memberSince}</small>
          </span>
          <LandingIcon name="chevronRight" size={14} className="qxl__profile-caret" />
        </button>
      </div>
    </header>
  );
}
